import * as React from "react";
import zip from "lodash/zip";
import { definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";

import type {
    PotentialValue,
    UI,
    ForceValueProducer,
    Loading,
    AppEnvironment} from "common/components/types";
import {
    LOADING,
    asUI,
    ComponentMoveAction
} from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import SimpleList from "../ui/views/simple-list/simple-list";
import { styled } from "../styled-components";
import { asArrayValue, asNumber, asObjectValue, asString, loadedDefinedMap } from "common/components/component-helpers";
import classNames from "classnames";
import type { ComponentLayout } from "common/generator/description";
import { getFeatureSetting } from "common/feature-settings";
import { GLIDE_COMPONENT_INDEX_DATA_FORMAT } from "sharedUI/lib/constants";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";

type Layout = ComponentLayout<UI>;
type ForcedLayout = ComponentLayout<React.ReactNode>;

function forceLayout(items: PotentialValue, force: ForceValueProducer): Layout | Loading {
    if (items === LOADING) return LOADING;

    const layout = mapFilterUndefined(asArrayValue(items), spanVP => {
        const spanValue = force(spanVP);
        if (spanValue === LOADING) return undefined;

        return mapFilterUndefined(asArrayValue(spanValue), spanColumnVP => {
            const spanColumnValue = force(spanColumnVP);
            if (spanColumnValue === LOADING) return undefined;

            const spanColumnObject = asObjectValue(spanColumnValue);
            const widthValue = definedMap(spanColumnObject.width, force);
            const componentsValue = definedMap(spanColumnObject.components, force);
            const idsValue = definedMap(spanColumnObject.componentIDs, force);
            if (widthValue === LOADING || componentsValue === LOADING || idsValue === LOADING) return undefined;

            return {
                width: asNumber(widthValue),
                components: mapFilterUndefined(
                    zip(asArrayValue(componentsValue), asArrayValue(idsValue)),
                    ([uiVP, idVP]) => {
                        const ui = loadedDefinedMap(definedMap(uiVP, force), asUI);
                        const id = loadedDefinedMap(definedMap(idVP, force), asString);
                        if (ui === undefined || id === undefined) return undefined;
                        return { ui, id };
                    }
                ),
            };
        });
    });

    return layout;
}

const SpanMaterialRendererStyle = styled.div`
    .new-top {
        ::after {
            pointer-events: none;
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 2px;

            background-color: ${p => p.theme.linkColor};
        }
    }

    .new-bottom {
        ::after {
            pointer-events: none;
            content: "";
            position: absolute;
            left: 0;
            bottom: 0;
            width: 100%;
            height: 2px;

            background-color: ${p => p.theme.linkColor};
        }
    }

    > .span-region {
        display: grid;
        grid-template-columns: repeat(12, minmax(0, 1fr));
        position: relative;

        .new-left {
            ::after {
                pointer-events: none;
                content: "";
                position: absolute;
                left: 0;
                top: 0;
                width: 2px;
                height: 100%;

                background-color: ${p => p.theme.linkColor};
            }
        }

        .new-right {
            ::after {
                pointer-events: none;
                content: "";
                position: absolute;
                right: 0;
                top: 0;
                width: 2px;
                height: 100%;

                background-color: ${p => p.theme.linkColor};
            }
        }

        > .span-column {
            position: relative;
            min-width: 0px;

            display: flex;
            flex-direction: column;

            .span-draggable {
                transform: translate3d(0, 0, 0);
            }

            .insert-after {
                position: relative;

                ::after {
                    pointer-events: none;
                    content: "";
                    position: absolute;
                    left: 0;
                    bottom: 0;
                    height: 2px;
                    width: 100%;

                    background-color: ${p => p.theme.linkColor};
                }
            }

            .insert-before {
                position: relative;

                ::after {
                    pointer-events: none;
                    content: "";
                    position: absolute;
                    left: 0;
                    top: 0;
                    height: 2px;
                    width: 100%;

                    background-color: ${p => p.theme.linkColor};
                }
            }
        }
    }
`;

interface SpanComponentProps {
    readonly layout: ForcedLayout;
    readonly appEnvironment: AppEnvironment;
}

const SpansComponent: React.FunctionComponent<SpanComponentProps> = p => {
    const { layout, appEnvironment } = p;

    const [dragSourceId, setDragSourceId] = React.useState<string>();
    const [dragHoverTarget, setDragHoverTarget] = React.useState<{ id: string; action: ComponentMoveAction }>();

    const onDragStart = React.useCallback((e: React.DragEvent<HTMLDivElement>, sourceId: string) => {
        if (getFeatureSetting("multiColumnLayout")) {
            e.dataTransfer.setData(GLIDE_COMPONENT_INDEX_DATA_FORMAT, sourceId);
            setDragSourceId(sourceId);
        }
    }, []);

    const onDragOver = React.useCallback(
        (e: React.DragEvent<HTMLDivElement>, targetId: string, isFirst: boolean, isLast: boolean) => {
            if (!e.dataTransfer.types.includes(GLIDE_COMPONENT_INDEX_DATA_FORMAT)) return;

            e.stopPropagation();
            e.preventDefault();

            const sourceId = dragSourceId;
            if (targetId === sourceId) {
                setDragHoverTarget(undefined);
                return;
            }

            const mouseX = e.clientX;
            const mouseY = e.clientY;
            const target = e.currentTarget;
            const bounds = target.getBoundingClientRect();

            const relX = mouseX - bounds.left;
            const relY = mouseY - bounds.top;

            const leftRightHitSize = Math.min(20, bounds.width / 3);
            const topBottomHitSize = Math.min(20, bounds.height / 3);

            let action = ComponentMoveAction.InsertIntoColumnAfter;
            if (relX < leftRightHitSize) {
                action = ComponentMoveAction.NewColumnLeft;
            } else if (relX > bounds.width - leftRightHitSize) {
                action = ComponentMoveAction.NewColumnRight;
            } else if (relY < topBottomHitSize) {
                action = isFirst ? ComponentMoveAction.NewSpanAbove : ComponentMoveAction.InsertIntoColumnBefore;
            } else if (relY > bounds.height - topBottomHitSize) {
                action = isLast ? ComponentMoveAction.NewSpanBelow : ComponentMoveAction.InsertIntoColumnAfter;
            }
            setDragHoverTarget({
                id: targetId,
                action,
            });
        },
        [dragSourceId]
    );

    const dragHoverTargetId = dragHoverTarget?.id;
    const onDragLeave = React.useCallback(
        (targetId: string) => {
            if (targetId === dragHoverTargetId) {
                setDragHoverTarget(undefined);
            }
        },
        [dragHoverTargetId]
    );

    const onDrop = React.useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            if (!e.dataTransfer.types.includes(GLIDE_COMPONENT_INDEX_DATA_FORMAT) || dragHoverTarget === undefined)
                return;

            e.stopPropagation();
            e.preventDefault();

            const sourceId = dragSourceId;

            if (sourceId !== undefined) {
                appEnvironment.componentMoved(sourceId, dragHoverTarget.id, dragHoverTarget.action);
            }

            setDragHoverTarget(undefined);
        },
        [appEnvironment, dragHoverTarget, dragSourceId]
    );

    const content = layout.map((span, spanIndex) => {
        let start = 1;
        const a = dragHoverTarget?.action;
        const spanHasDropTarget =
            span.find(s => s.components.find(c => c.id === dragHoverTargetId) !== undefined) !== undefined;
        return (
            <div
                key={spanIndex}
                className={classNames(
                    "span-region",
                    spanHasDropTarget && a === ComponentMoveAction.NewSpanAbove && "new-top",
                    spanHasDropTarget && a === ComponentMoveAction.NewSpanBelow && "new-bottom"
                )}>
                {span.map((column, columnIndex) => {
                    const style: React.CSSProperties = {
                        gridArea: `1 / ${start} / span 1 / span ${column.width}`,
                    };
                    start += column.width;

                    const colHasDropTarget =
                        spanHasDropTarget && column.components.find(c => c.id === dragHoverTargetId) !== undefined;

                    return (
                        <div
                            className={classNames(
                                "span-column",
                                colHasDropTarget && a === ComponentMoveAction.NewColumnLeft && "new-left",
                                colHasDropTarget && a === ComponentMoveAction.NewColumnRight && "new-right"
                            )}
                            key={columnIndex}
                            style={style}>
                            <SimpleList items={column.components}>
                                {(v, i) => {
                                    const isTarget = dragHoverTargetId === v.id;
                                    return (
                                        <div
                                            className={classNames(
                                                "span-draggable",
                                                isTarget &&
                                                    a === ComponentMoveAction.InsertIntoColumnAfter &&
                                                    "insert-after",
                                                isTarget &&
                                                    a === ComponentMoveAction.InsertIntoColumnBefore &&
                                                    "insert-before"
                                            )}
                                            draggable={true}
                                            onDragStart={e => onDragStart(e, v.id)}
                                            onDragOver={e =>
                                                onDragOver(e, v.id, i === 0, i === column.components.length - 1)
                                            }
                                            onDragLeave={() => onDragLeave(v.id)}
                                            onDragExit={() => onDragLeave(v.id)}
                                            onDrop={onDrop}
                                            key={i}>
                                            {v.ui}
                                        </div>
                                    );
                                }}
                            </SimpleList>
                        </div>
                    );
                })}
            </div>
        );
    });

    return <SpanMaterialRendererStyle>{content}</SpanMaterialRendererStyle>;
};

export const SpansMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[itemsValue]] = useForceValueProducers(getValueProducers, force, ctx);
    const layout = forceLayout(itemsValue, force);

    if (layout === LOADING) {
        return env.renderProgress();
    }

    const forcedLayout: ForcedLayout = layout.map(span =>
        span.map(col => ({
            ...col,
            components: col.components.map(c => ({
                ...c,
                ui: asUI(c.ui).render(ctx, env),
            })),
        }))
    );

    return <SpansComponent appEnvironment={env.appEnvironment} layout={forcedLayout} />;
};
